import { inject } from 'vue';

import { cloneDeep } from 'lodash';

import { defineStore } from 'pinia';
import { CreateANewMemoCommand } from 'src/stores/memo/commands/CreateANewMemoCommand';
import { DeleteMemoCommand } from 'src/stores/memo/commands/DeleteMemoCommand';
import { SaveMemoCommand } from 'src/stores/memo/commands/SaveMemoCommand';
import { SavePersonalMemoCommand } from 'src/stores/memo/commands/SavePersonalMemoCommand';
import { MemoQuery, PersonalMemoQuery } from 'src/stores/memo/queries';

import { CommandProcessIdHandling } from 'src/models/command';
import { Memo, MemoRunType, PersonalMemo, PersonalMemoRunType } from 'src/models/memo';
import { SetTheStateForMultipleAggregates } from 'src/models/status/SetStatusCommand';
import { StatusEnum } from 'src/models/status/enums';

import { AggregateComparator } from 'src/util/aggregate/AggregateComparator';
import { AggregateMerger } from 'src/util/aggregate/AggregateMerger';
import {
	ApplyTheStatusForTheAggregateIdsPayload,
	aggregateIdWithUnsetSourceAndTargetContainerId,
	applyTheStatusForTheAggregateIdsInRecordMaps,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { CommandBus } from 'src/util/command/CommandBus';
import { releaseRecord, retainRecord } from 'src/util/records/recordLeases';

import { MemoCallback, PersonalMemoCallback } from './callbacks';
import {
	CreateTheLinkedMemoPayload,
	DeleteTheMemoPayload,
	FetchMemoPayload,
	FetchUserNotepadMemoPayload,
	MemoStoreState,
	ReleaseMemoPayload,
	ResetPayload,
	RetainMemoPayload,
	SaveTheMemoPayload,
	SaveUserNotepadMemoPayload,
	SetTheMemoStatePayload,
	UnsubscribeFetchMemoPayload,
	UpdateTheMemoAlarmPayload,
	UpdateTheMemoAuthorUserIdPayload,
	UpdateTheMemoCommunicationTypeIdPayload,
	UpdateTheMemoContactPersonUserIdPayload,
	UpdateTheMemoContentPayload,
	UpdateTheMemoDurationPayload,
	UpdateTheMemoTimestampPayload,
	UpdateTheMemoTitlePayload,
} from './interfaces';

const normalizeAggregateId = (aggregateId: string): string =>
	aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId);

const USER_NOTEPAD_TEXT_KEY = 'userNotepadText';
const USER_NOTEPAD_WIDTH_KEY = 'userNotepadWidth';
const USER_NOTEPAD_HEIGHT_KEY = 'userNotepadHeight';
const DEFAULT_NOTEPAD_WIDTH = 300;
const DEFAULT_NOTEPAD_HEIGHT = 400;

const readNumberFromLocalStorage = (key: string, fallback: number): number => {
	if (typeof localStorage === 'undefined') {
		return fallback;
	}

	const stored = Number(localStorage.getItem(key));
	return stored > 0 ? stored : fallback;
};

const createDefaultState = (): MemoStoreState => ({
	initialLoadedMemos: new Map<string, Memo>(),
	isNotepadOpen: false,
	memos: new Map<string, Memo>(),
	newMemoAggregateId: '',
	userNotepadHeight: readNumberFromLocalStorage(USER_NOTEPAD_HEIGHT_KEY, DEFAULT_NOTEPAD_HEIGHT),
	userNotepadText: typeof localStorage !== 'undefined' ? (localStorage.getItem(USER_NOTEPAD_TEXT_KEY) ?? '') : '',
	userNotepadWidth: readNumberFromLocalStorage(USER_NOTEPAD_WIDTH_KEY, DEFAULT_NOTEPAD_WIDTH),
});

const resolveStateCommandPayload = (
	payload: SetTheMemoStatePayload | string
): { aggregateId: string; commandBus: CommandBus | undefined } => {
	if (typeof payload === 'string') {
		return {
			aggregateId: payload,
			commandBus: inject<CommandBus>('commandBus'),
		};
	}

	return {
		aggregateId: payload.aggregateId,
		commandBus: payload.commandBus ?? inject<CommandBus>('commandBus'),
	};
};

export const useMemoStore = defineStore('memo', {
	actions: {
		applyTheStatusForTheAggregateIds(payload: ApplyTheStatusForTheAggregateIdsPayload): void {
			applyTheStatusForTheAggregateIdsInRecordMaps(
				[this.memos, this.initialLoadedMemos],
				payload.aggregateIds,
				payload.status
			);
		},
		createTheLinkedMemo(payload: CreateTheLinkedMemoPayload): void {
			void payload.commandBus
				?.sendCommand(new CreateANewMemoCommand(String(payload.idoId), String(payload.instanceId)))
				?.then((result) => {
					const aggregateId = (
						result as {
							data?: {
								data?: { results?: { aggregateId?: string } };
							};
						}
					).data?.data?.results?.aggregateId;
					this.setNewMemoAggregateId(aggregateId ?? '');
				});
		},
		async deleteTheMemo(payload: DeleteTheMemoPayload): Promise<void> {
			await this.unsubscribeFetchMemo({
				aggregateId: payload.aggregateId,
				queryStore: payload.queryStore,
			});

			void payload.commandBus?.sendCommand(new DeleteMemoCommand(payload.aggregateId));
		},
		discardChanges(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const baseline = this.initialLoadedMemos.get(normalizedAggregateId);
			if (baseline === undefined) {
				return;
			}

			this.memos.set(normalizedAggregateId, cloneDeep<Memo>(baseline));
		},
		fetchMemo(payload: FetchMemoPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				return payload.queryStore.query<Memo>({
					callback: new MemoCallback((memo) => {
						this.setMemo(memo);
					}),
					createTime: new Date(),
					query: new MemoQuery(payload.aggregateId),
					responseType: 'json',
					runType: MemoRunType,
				});
			}

			this.setMemo(null);
			return Promise.resolve();
		},
		fetchUserNotepadMemo(payload: FetchUserNotepadMemoPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				return Promise.resolve();
			}

			return payload.queryStore.query<PersonalMemo | PersonalMemo[]>({
				callback: new PersonalMemoCallback((memo) => {
					this.setUserNotepadText(memo.text);
					this.setUserNotepadSize(memo.width, memo.height);
				}),
				createTime: new Date(),
				query: new PersonalMemoQuery(),
				responseType: 'json',
				runType: PersonalMemoRunType,
			});
		},
		initialLoadedMemo(aggregateId: string): Memo | undefined {
			return this.initialLoadedMemos.get(normalizeAggregateId(aggregateId));
		},
		markMemoAsSaved(aggregateId: string): void {
			const normalizedAggregateId = normalizeAggregateId(aggregateId);
			const memoFromCollection = this.memos.get(normalizedAggregateId);
			if (memoFromCollection !== undefined) {
				this.initialLoadedMemos.set(normalizedAggregateId, cloneDeep<Memo>(memoFromCollection));
			}
		},
		memo(aggregateId: string): Memo | undefined {
			return this.memos.get(aggregateId);
		},
		release(payload: ReleaseMemoPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const wasTheLastLease = releaseRecord(`memo:${normalizedAggregateId}`);
			if (!wasTheLastLease || payload.shouldKeepData) {
				return;
			}

			this.memos.delete(normalizedAggregateId);
			this.initialLoadedMemos.delete(normalizedAggregateId);
			void this.unsubscribeFetchMemo({
				aggregateId: payload.aggregateId,
				queryStore: payload.queryStore,
			});
		},
		reset(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);

			this.memos.delete(normalizedAggregateId);
			this.initialLoadedMemos.delete(normalizedAggregateId);
			this.newMemoAggregateId = '';
		},
		retain(payload: RetainMemoPayload): void {
			retainRecord(`memo:${normalizeAggregateId(payload.aggregateId)}`);
		},
		async saveTheMemo(payload: SaveTheMemoPayload): Promise<boolean> {
			const memoFromCollection = this.memos.get(normalizeAggregateId(payload.aggregateId));

			if (memoFromCollection === undefined) {
				return false;
			}

			const response = await payload.commandBus?.sendCommand(new SaveMemoCommand(memoFromCollection));

			return response !== null && response !== undefined;
		},
		saveUserNotepadMemo(payload: SaveUserNotepadMemoPayload): void {
			this.setUserNotepadText(payload.text);
			this.setUserNotepadSize(payload.width, payload.height);

			void payload.commandBus?.sendCommand(
				new SavePersonalMemoCommand(payload.text, payload.width, payload.height)
			);
		},
		setMemo(payload: Memo | null): void {
			if (payload === null) {
				return;
			}

			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const memoFromCollection = this.memos.get(normalizedAggregateId);
			const initialLoadedMemoFromCollection = this.initialLoadedMemos.get(normalizedAggregateId);

			if (memoFromCollection !== undefined && !AggregateComparator.isEqual(memoFromCollection, payload)) {
				return;
			}

			if (memoFromCollection !== undefined && initialLoadedMemoFromCollection !== undefined) {
				const mergedMemo = AggregateMerger.merge(
					initialLoadedMemoFromCollection,
					memoFromCollection,
					payload
				) as Memo;

				this.memos.set(normalizeAggregateId(mergedMemo.aggregateId), cloneDeep(mergedMemo));
			} else {
				this.memos.set(normalizedAggregateId, payload);
			}

			this.initialLoadedMemos.set(normalizedAggregateId, cloneDeep(payload));
		},
		setNewMemoAggregateId(payload: string): void {
			this.newMemoAggregateId = payload ?? '';
		},
		setNotepadOpen(value: boolean): void {
			this.isNotepadOpen = value;
		},
		setTheMemoStateToReady(payload: SetTheMemoStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.READY
				)
			);
		},
		setTheMemoStateToSeen(payload: SetTheMemoStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.SEEN
				)
			);
		},
		setTheMemoStateToUnseen(payload: SetTheMemoStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.UNSEEN
				)
			);
		},
		setUserNotepadSize(width: number, height: number): void {
			this.userNotepadWidth = width > 0 ? width : DEFAULT_NOTEPAD_WIDTH;
			this.userNotepadHeight = height > 0 ? height : DEFAULT_NOTEPAD_HEIGHT;
			if (typeof localStorage !== 'undefined') {
				localStorage.setItem(USER_NOTEPAD_WIDTH_KEY, String(this.userNotepadWidth));
				localStorage.setItem(USER_NOTEPAD_HEIGHT_KEY, String(this.userNotepadHeight));
			}
		},
		setUserNotepadText(text: string): void {
			this.userNotepadText = text;
			if (typeof localStorage !== 'undefined') {
				localStorage.setItem(USER_NOTEPAD_TEXT_KEY, text);
			}
		},
		toggleNotepad(): void {
			this.isNotepadOpen = !this.isNotepadOpen;
		},
		async unsubscribeFetchMemo(payload: UnsubscribeFetchMemoPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.unsubscribeQuery(new MemoQuery(payload.aggregateId));
			}

			this.setMemo(null);
		},
		updateTheMemoAlarm(payload: UpdateTheMemoAlarmPayload): void {
			const memoFromCollection = this.memos.get(normalizeAggregateId(payload.aggregateId));

			if (memoFromCollection !== undefined) {
				memoFromCollection.alarm = payload.alarm;
				this.memos.set(normalizeAggregateId(memoFromCollection.aggregateId), memoFromCollection);
			}
		},
		updateTheMemoAuthorUserId(payload: UpdateTheMemoAuthorUserIdPayload): void {
			const memoFromCollection = this.memos.get(normalizeAggregateId(payload.aggregateId));

			if (memoFromCollection !== undefined) {
				memoFromCollection.authorId = payload.authorUserId;
				this.memos.set(normalizeAggregateId(memoFromCollection.aggregateId), memoFromCollection);
			}
		},
		updateTheMemoCommunicationTypeId(payload: UpdateTheMemoCommunicationTypeIdPayload): void {
			const memoFromCollection = this.memos.get(normalizeAggregateId(payload.aggregateId));

			if (memoFromCollection !== undefined) {
				memoFromCollection.communicationTypeId = payload.communicationType;
				this.memos.set(normalizeAggregateId(memoFromCollection.aggregateId), memoFromCollection);
			}
		},
		updateTheMemoContactPersonUserId(payload: UpdateTheMemoContactPersonUserIdPayload): void {
			const memoFromCollection = this.memos.get(normalizeAggregateId(payload.aggregateId));

			if (memoFromCollection !== undefined) {
				memoFromCollection.contactPersonId = payload.contactPersonUserId as number;
				this.memos.set(normalizeAggregateId(memoFromCollection.aggregateId), memoFromCollection);
			}
		},
		updateTheMemoContent(payload: UpdateTheMemoContentPayload): void {
			const memoFromCollection = this.memos.get(normalizeAggregateId(payload.aggregateId));

			if (memoFromCollection !== undefined) {
				memoFromCollection.content = payload.content;
				this.memos.set(normalizeAggregateId(memoFromCollection.aggregateId), memoFromCollection);
			}
		},
		updateTheMemoDuration(payload: UpdateTheMemoDurationPayload): void {
			const memoFromCollection = this.memos.get(normalizeAggregateId(payload.aggregateId));

			if (memoFromCollection !== undefined) {
				memoFromCollection.duration = payload.duration;
				this.memos.set(normalizeAggregateId(memoFromCollection.aggregateId), memoFromCollection);
			}
		},
		updateTheMemoTimestamp(payload: UpdateTheMemoTimestampPayload): void {
			const memoFromCollection = this.memos.get(normalizeAggregateId(payload.aggregateId));

			if (memoFromCollection !== undefined) {
				memoFromCollection.timestamp = payload.timestamp;
				this.memos.set(normalizeAggregateId(memoFromCollection.aggregateId), memoFromCollection);
			}
		},
		updateTheMemoTitle(payload: UpdateTheMemoTitlePayload): void {
			const memoFromCollection = this.memos.get(normalizeAggregateId(payload.aggregateId));

			if (memoFromCollection !== undefined) {
				memoFromCollection.title = payload.title.replace(/\n/g, ' ');
				this.memos.set(normalizeAggregateId(memoFromCollection.aggregateId), memoFromCollection);
			}
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
